import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL, USER_URL } from "../../constant/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ScaleLoader } from "react-spinners"; // Import ScaleLoader

function CreateSubCategory() {
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryName, setSubCategoryName] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await USER_URL.get(`/categories/`);
        const categoriesData = response.data.Data || response.data;
        if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
        } else {
          toast.error("Invalid categories data format");
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories");
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Handle Image Upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  // Form Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!categoryId || !subCategoryName || !image) {
      toast.warn("Please fill in all fields");
      return;
    }

    const formData = new FormData();
    formData.append("category", categoryId);
    formData.append("name", subCategoryName);
    formData.append("image", image);

    try {
      setIsSubmitting(true);
      
      const response = await USER_URL.post(
        `/categories/admin/subcategories/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success("Subcategory added successfully!");
      console.log("Response:", response.data);
      
      // Clear form
      setCategoryId("");
      setSubCategoryName("");
      setImage(null);
      setPreview(null);
    } catch (error) {
      console.error("Error adding subcategory:", error);
      toast.error(error.response?.data?.message || "Failed to add subcategory.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while fetching categories
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <ScaleLoader color="#FFAE00" size={40} />
      </div>
    );
  }

  return (
    <div className="p-3">
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="border-2 rounded-lg shadow-lg bg-white p-16 pt-8 w-full">
        <h2 className="text-green-600 text-3xl font-bold text-center mb-8 p-5">Create Sub Category</h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-8">
          {/* Image Upload */}
          <div className="flex flex-col">
            <label className="text-md font-medium mb-2">Sub category Image</label>
            <div className="w-44 h-44 border flex items-center justify-center rounded-lg overflow-hidden bg-gray-100">
              {preview ? (
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-center text-gray-500 text-sm">No image selected</span>
              )}
            </div>
            <label className="flex items-center cursor-pointer text-white bg-blue-500 px-4 py-2 mt-3 w-44 rounded-md hover:bg-blue-600 text-sm">
              <FaCloudUploadAlt className="mr-2 text-lg" />
              Upload Image
              <input 
                type="file" 
                onChange={handleImageChange} 
                className="hidden" 
                accept="image/*" 
              />
            </label>
          </div>

          {/* Category and Subcategory Name */}
          <div className="flex flex-col">
            <label className="text-md font-medium mb-2">Category Name</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="border mb-5 px-3 py-2 rounded focus:outline-none focus:ring focus:ring-green-200"
            >
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            <label className="text-md font-medium mb-2">Subcategory Name</label>
            <input
              type="text"
              value={subCategoryName}
              onChange={(e) => setSubCategoryName(e.target.value)}
              placeholder="Enter Subcategory name"
              className="border mb-5 px-3 py-2 rounded focus:outline-none focus:ring focus:ring-green-200"
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-green-600 text-white py-2 rounded mt-4 transition ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-green-700"
              }`}
            >
              {isSubmitting ? (
                <div className="flex justify-center">
                  <ScaleLoader color="#ffffff" height={20} width={3} radius={2} margin={2} />
                </div>
              ) : "Create Subcategory"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateSubCategory;
